import {
	AutoComplete,
	GroupInput,
	IInputProps,
	Input,
	ISelect,
	Select,
} from '@/components';
import { IAutoComplete } from '@/components/form/auto-complete';
import { insertObjectIfV2 } from '@/utils';
import { memo, useCallback } from 'react';

import { FormikProps } from 'formik';

import { ISearchTypeConfig } from './type';

export interface IInputTarget {
	formik: FormikProps<{ [key: string]: any }>;
	label: string;
	name: string;
	labelClassName?: string;
	containerClassName?: string;
	inputProps?: Partial<IInputProps>;
	dropdownProps?: Partial<ISelect>;
	autoCompleteProps?: Partial<IAutoComplete>;
	searchTypeConfigs?: ISearchTypeConfig[];
	onSearch?: () => void;
}

const InputTarget = ({
	formik,
	label,
	name,
	labelClassName,
	containerClassName,
	inputProps,
	dropdownProps,
	autoCompleteProps,
	searchTypeConfigs,
	onSearch,
	...props
}: IInputTarget) => {
	const _onChangeSelect = useCallback(
		(e: any) => {
			formik.setValues((prev) => ({
				...prev,
				[name]: {
					...prev[name],
					searchType: e,
					searchValue: '',
				},
			}));
		},
		[formik, name]
	);
	const handleChangeSearchValue = useCallback(
		(value: string) => {
			formik.setValues((prev) => ({
				...prev,
				[name]: {
					...prev[name],
					searchValue: value,
				},
			}));
		},
		[formik, name]
	);

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			handleChangeSearchValue(e.target.value);
		},
		[handleChangeSearchValue]
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter') {
				onSearch?.();
			}
		},
		[onSearch]
	);

	const renderSearchInput = useCallback(() => {
		const currentSearchType = formik.values[name]?.searchType;
		const searchTypeConfig = searchTypeConfigs?.find(
			(config) => config.value === currentSearchType
		);

		const commonProps = {
			inputClassName: '!w-full',
			containerClassName: '!w-[200px]',
			value: formik.values[name]?.searchValue || '',
		};

		if (searchTypeConfig?.inputType === 'autocomplete') {
			return (
				<AutoComplete
					{...commonProps}
					{...autoCompleteProps}
					{...insertObjectIfV2(searchTypeConfig?.type, {
						type: searchTypeConfig?.type,
					})}
					options={autoCompleteProps?.options}
					searchValue={commonProps.value}
					onChange={handleChangeSearchValue}
					onSelect={onSearch}
					onKeyDown={handleKeyDown}
				/>
			);
		}

		return (
			<Input
				{...commonProps}
				{...inputProps}
				name={name}
				onChange={handleInputChange}
				onPressEnter={onSearch}
			/>
		);
	}, [
		formik.values,
		name,
		searchTypeConfigs,
		autoCompleteProps,
		inputProps,
		handleChangeSearchValue,
		handleInputChange,
		handleKeyDown,
		onSearch,
	]);

	return (
		<GroupInput
			label={label}
			labelClassName={labelClassName}
			containerClassName={containerClassName}
			groupClassName="flex-none gap-x-1.5"
		>
			<Select
				value={formik.values[name]?.searchType}
				{...dropdownProps}
				onChange={_onChangeSelect}
				containerClassName="!w-[200px]"
			/>
			{renderSearchInput()}
		</GroupInput>
	);
};

export default memo(InputTarget);
